import type { ReactNode } from "react"
import { Link } from "@inertiajs/react"
import { ChevronRight } from "lucide-react"

import { cn } from "@/lib/utils"
import { formatMoney } from "./money"
import { t } from "@/lib/i18n"

type StatusCategory = "ready" | "active" | "done" | "cancelled"

const STATUS_DOT: Record<StatusCategory, string> = {
  ready: "bg-status-available",
  active: "bg-status-occupied",
  done: "bg-muted-foreground/50",
  cancelled: "bg-destructive",
}

const STATUS_CHIP: Record<StatusCategory, string> = {
  ready: "bg-status-available/12",
  active: "bg-status-occupied/12",
  done: "bg-muted",
  cancelled: "bg-destructive/12",
}

export function statusCategory(status: string): StatusCategory {
  if (["ready", "ready_for_pickup"].includes(status)) return "ready"
  if (["served", "completed", "picked_up", "closed", "paid"].includes(status)) return "done"
  if (status === "cancelled") return "cancelled"
  return "active"
}

/** Status pill: the dot carries the color, the label stays readable text. */
export function StatusChip({ status, label }: { status: string; label: string }) {
  const category = statusCategory(status)

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium text-foreground",
        STATUS_CHIP[category]
      )}
    >
      <span className={cn("size-1.5 rounded-full", STATUS_DOT[category])} aria-hidden="true" />
      {label}
    </span>
  )
}

/** Neutral pill for the non-status facts on a line (service type, table, who). */
export function MetaChip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground",
        className
      )}
    >
      {children}
    </span>
  )
}

type OrderLineProps = {
  testId: string
  quantity: number
  name: string
  /** Modifiers or notes, already collapsed into one line. */
  detail?: string | null
  status: string
  statusLabel?: string | null
  lineTotal: string
  currency: string
  /**
   * What came off this line, as an exact decimal string. Omit or pass `"0.0"`
   * when nothing did.
   *
   * `lineTotal` is what gets charged, and a diner comparing it to the menu has
   * been reading an unexplained gap ever since discounts shipped. For a
   * promotion the gap *is* the value proposition, so it gets its own line rather
   * than a smaller total nobody can account for.
   */
  discountAmount?: string | null
  /** The rule's name, when a rule produced the reduction. */
  discountLabel?: string | null
  /** Extra pills beside the status chip. */
  chips?: ReactNode
  /** Turns the row into a link and grows a chevron. */
  href?: string
}

/**
 * One ordered dish, rendered the same way wherever it shows up — the bill and
 * the order history are two views of the same fact, so a diner comparing them
 * should not have to re-learn the layout.
 */
export function OrderLine({
  testId,
  quantity,
  name,
  detail,
  status,
  statusLabel,
  lineTotal,
  currency,
  discountAmount,
  discountLabel,
  chips,
  href,
}: OrderLineProps) {
  const reduction = Number(discountAmount ?? 0)
  const body = (
    <>
      <span className="w-7 shrink-0 text-sm font-semibold tabular-nums text-muted-foreground">
        {quantity}×
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-tight tracking-tight">{name}</p>
        {detail && (
          <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-muted-foreground">{detail}</p>
        )}
        {reduction > 0 && (
          <p
            className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground"
            data-testid={`${testId}-discount`}
          >
            {/* Colour in the mark, never in the figure — the reduction is a
                number and stays one, muted like the rest of the line's meta. */}
            <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-success" />
            <span className="min-w-0 truncate">
              {discountLabel || t("ui.react.features.user.order_line.discount")} · −
              <span className="tabular-nums">{formatMoney(reduction, { currency })}</span>
            </span>
          </p>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1">
          {statusLabel && <StatusChip status={status} label={statusLabel} />}
          {chips}
        </div>
      </div>
      <span className="shrink-0 text-sm font-semibold tabular-nums">
        {formatMoney(lineTotal, { currency })}
      </span>
      {href && <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />}
    </>
  )

  return (
    <li className="border-b border-border/60 last:border-b-0" data-testid={testId}>
      {href ? (
        <Link
          href={href}
          className="-mx-2 flex items-start gap-3 rounded-lg px-2 py-3.5 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {body}
        </Link>
      ) : (
        <div className="flex items-start gap-3 py-3.5">{body}</div>
      )}
    </li>
  )
}
